import _ from 'lodash'

import Transport from '@/lib/api/transport'
import ApiBase from '@/lib/api/v1/api-base'

import {
  ApiInventoryThresholdPayload,
  ApiCurrentInventoryResponse,
  ApiInventoryLogResponse,
  ApiInventoryThresholdResponse,
  ApiCreateInventoryThresholdValidationPayload,
  ApiEditInventoryThresholdValidationPayload,
  ApiOrderTemplatesResponse,
  ApiOrderTemplatesPayload,
  ApiOrderTemplate,
  ApiOrderTemplatesValidationResponse,
  ApiDepartmentsResponse,
  ApiCompanySkusResponse,
} from '@/types'

class InventoryTracking extends ApiBase {
  constructor(transport: Transport) {
    super(transport)
    this.getInventoryThresholds = this.getInventoryThresholds.bind(this)
    this.createInventoryThreshold = this.createInventoryThreshold.bind(this)
    this.updateInventoryThreshold = this.updateInventoryThreshold.bind(this)
    this.validateThreshold = this.validateThreshold.bind(this)
    this.getInventoryLogs = this.getInventoryLogs.bind(this)
    this.addNewOrderTemplate = this.addNewOrderTemplate.bind(this)
    this.updateOrderTemplate = this.updateOrderTemplate.bind(this)
    this.getDepartments = this.getDepartments.bind(this)
    this.getCompanySkus = this.getCompanySkus.bind(this)
    this.placeQuoteForThreshold = this.placeQuoteForThreshold.bind(this)
  }

  getInventoryThresholds(params: object, userId?: string) {
    return this._withDecoratedPromise<ApiInventoryThresholdResponse>(() => {
      const uniqueParams = _.pickBy(params) as Record<string, string>
      const paramsString = new URLSearchParams(uniqueParams).toString()
      return this.transport.get(
        `/rest/api/v1/users/${this._getUserId(userId)}/its/threshold?${paramsString}`,
      )
    })
  }

  getCurrentInventory(params: object, userId?: string) {
    return this._withDecoratedPromise<ApiCurrentInventoryResponse>(() => {
      const uniqueParams = _.pickBy(params) as Record<string, string>
      const paramsString = new URLSearchParams(uniqueParams).toString()
      return this.transport.get(
        `/rest/api/v1/users/${this._getUserId(userId)}/its/inventory?${paramsString}`,
      )
    })
  }

  createInventoryThreshold(thresholdData: ApiInventoryThresholdPayload, userId?: string) {
    return this._withDecoratedPromise<string>(() => {
      return this.transport.post(
        `/rest/api/v1/users/${this._getUserId(userId)}/its/threshold`,
        thresholdData,
      )
    })
  }

  updateInventoryThreshold(thresholdData: ApiInventoryThresholdPayload, userId?: string) {
    return this._withDecoratedPromise<string>(() => {
      return this.transport.put(
        `/rest/api/v1/users/${this._getUserId(userId)}/its/threshold`,
        thresholdData,
      )
    })
  }

  validateThreshold(
    thresholdData:
      | ApiCreateInventoryThresholdValidationPayload
      | ApiEditInventoryThresholdValidationPayload,
    userId?: string,
  ) {
    return this._withDecoratedPromise(() => {
      return this.transport.put(
        `/rest/api/v1/users/${this._getUserId(userId)}/its/validate/threshold`,
        thresholdData,
      )
    })
  }

  getInventoryLogs(
    params: { searchTerm: string; currentPage: number; pageSize: number },
    userId?: string,
  ) {
    return this._withDecoratedPromise<ApiInventoryLogResponse>(() => {
      const uniqueParams = _.pickBy(params) as Record<string, string>
      const paramsString = new URLSearchParams(uniqueParams).toString()
      return this.transport.get(
        `/rest/api/v1/users/${this._getUserId(userId)}/its/inventory/log?${paramsString}`,
      )
    })
  }

  getOrderTemplates(
    params: {
      searchTerm?: string
      filter?: string
      sort?: string
      currentPage?: number
      pageSize?: number
      company?: string
      department?: string
    },
    userId?: string,
  ) {
    return this._withDecoratedPromise<ApiOrderTemplatesResponse>(() => {
      const uniqueParams = _.pickBy(params) as Record<string, string>
      const paramsString = new URLSearchParams(uniqueParams).toString()
      return this.transport.get(
        `/rest/api/v1/users/${this._getUserId(userId)}/its/order-template?${paramsString}`,
      )
    })
  }

  addNewOrderTemplate(orderTemplateData: ApiOrderTemplatesPayload, userId?: string) {
    return this._withDecoratedPromise<ApiOrderTemplate>(() => {
      return this.transport.post(
        `/rest/api/v1/users/${this._getUserId(userId)}/its/order-template`,
        orderTemplateData,
      )
    })
  }

  editCurrentInventory(
    {
      quantity,
      departmentId,
      inventoryId,
    }: { quantity: number; departmentId: number; inventoryId: number },
    userId?: string,
  ) {
    return this._withDecoratedPromise<string>(() => {
      return this.transport.patch(
        `/rest/api/v1/users/${this._getUserId(
          userId,
        )}/its/department/${departmentId}/id/${inventoryId}`,
        quantity,
      )
    })
  }

  updateOrderTemplate(orderTemplateData: ApiOrderTemplatesPayload, userId?: string) {
    return this._withDecoratedPromise<ApiOrderTemplate>(() => {
      return this.transport.put(
        `/rest/api/v1/users/${this._getUserId(userId)}/its/order-template`,
        orderTemplateData,
      )
    })
  }

  validateOrderTemplate(orderTemplateData: ApiOrderTemplatesPayload, userId?: string) {
    return this._withDecoratedPromise<ApiOrderTemplatesValidationResponse>(() => {
      return this.transport.put(
        `/rest/api/v1/users/${this._getUserId(userId)}/its/validate/order-template`,
        orderTemplateData,
      )
    })
  }

  getDepartments(
    params: {
      pageSize?: number
      company: string
      templateId?: string
      writeAccess?: boolean
    },
    userId?: string,
  ) {
    return this._withDecoratedPromise<ApiDepartmentsResponse>(() => {
      const uniqueParams = _.pickBy(params) as Record<string, string>
      const paramsString = new URLSearchParams(uniqueParams).toString()
      return this.transport.get(
        `/rest/api/v1/users/${this._getUserId(userId)}/its/department?${paramsString}`,
      )
    })
  }

  getCompanySkus(
    {
      companyId,
      params,
    }: {
      companyId: string
      params: {
        currentPage: number
        pageSize: number
        filter: string
      }
    },
    userId?: string,
  ) {
    return this._withDecoratedPromise<ApiCompanySkusResponse>(() => {
      const uniqueParams = _.pickBy(params) as Record<string, string>
      const paramsString = new URLSearchParams(uniqueParams).toString()
      return this.transport.get(
        `/rest/api/v1/users/${this._getUserId(userId)}/its/company/${encodeURIComponent(
          companyId,
        )}/sku?${paramsString}`,
      )
    })
  }

  downloadCurrentInventory(searchTerm: string, userId?: string) {
    const requestOptions = {
      responseType: 'arraybuffer' as const,
    }
    return this._withDecoratedPromise<BlobPart>(() => {
      return this.transport.get(
        `/rest/api/v1/users/${this._getUserId(userId)}/its/export?searchTerm=${searchTerm}`,
        undefined,
        null,
        requestOptions,
      )
    })
  }

  downloadInventoryLog(param: string, userId?: string) {
    const requestOptions = {
      responseType: 'arraybuffer' as const,
    }
    return this._withDecoratedPromise(() => {
      return this.transport.get(
        `/rest/api/v1/users/${this._getUserId(userId)}/its/inventory/log/export?${param}`,
        undefined,
        null,
        requestOptions,
      )
    })
  }

  placeQuoteForThreshold(
    payload: {
      additionalInformation: string
      customizations: Array<{
        selected: string
        skuAttribute: boolean
        title: string
      }>
      quantity: number
      thresholdId: string
    },
    userId?: string,
  ) {
    return this._withDecoratedPromise<string>(() => {
      return this.transport.post(`/rest/api/v1/users/${this._getUserId(userId)}/its/quote`, payload)
    })
  }
}

export default InventoryTracking
